if (!customElements.get('mobile-variant-sheet')) {
  class MobileVariantSheet extends HTMLElement {
    constructor() {
      super();
      this.currentPanel = null;
      this.isOpen = false;
      this.overlay = null;
      this.handleTriggerClick = this.handleTriggerClick.bind(this);
      this.handleColorInlineClick = () => this.openPanel('color');
      this.handleOverlayClick = () => this.close();
      this.handleHandleTap = this.handleHandleTap.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.dragStartY = 0;
      this.dragCurrentY = 0;
      this.isDragging = false;
      this.scrollY = 0;
    }

    connectedCallback() {
      this.trigger = this.querySelector('[data-sheet-trigger]');
      this.panels = this.querySelectorAll('[data-sheet-panel]');
      this.sizeButtons = this.querySelectorAll('[data-size-value]');
      this.expandedEl = this.querySelector('.mobile-sheet__expanded');
      this.collapsedEl = this.querySelector('.mobile-sheet__collapsed');
      this.variantsData = JSON.parse(this.querySelector('[data-sheet-variants]')?.textContent || '[]');
      this.sectionId = this.dataset.sectionId;

      this.productInfo = this.closest('product-info');
      this.handleEl = this.querySelector('[data-sheet-handle]');

      this.overlay = document.createElement('div');
      this.overlay.className = 'mobile-sheet-overlay';
      document.body.appendChild(this.overlay);

      this.trigger?.addEventListener('click', this.handleTriggerClick);
      this.sizeButtons.forEach(btn => btn.addEventListener('click', (e) => this.handleSizeSelect(e)));
      this.overlay.addEventListener('click', this.handleOverlayClick);

      this.colorInlineBtn = this.querySelector('[data-open-panel="color"]');
      this.colorInlineBtn?.addEventListener('click', this.handleColorInlineClick);

      if (this.handleEl) {
        this.handleEl.addEventListener('touchstart', this.onTouchStart, { passive: true });
        this.handleEl.addEventListener('touchmove', this.onTouchMove, { passive: false });
        this.handleEl.addEventListener('touchend', this.onTouchEnd);
        this.handleEl.addEventListener('click', this.handleHandleTap);
      }
    }

    disconnectedCallback() {
      this.trigger?.removeEventListener('click', this.handleTriggerClick);
      this.colorInlineBtn?.removeEventListener('click', this.handleColorInlineClick);
      this.overlay?.removeEventListener('click', this.handleOverlayClick);
      if (this.handleEl) {
        this.handleEl.removeEventListener('touchstart', this.onTouchStart);
        this.handleEl.removeEventListener('touchmove', this.onTouchMove);
        this.handleEl.removeEventListener('touchend', this.onTouchEnd);
        this.handleEl.removeEventListener('click', this.handleHandleTap);
      }
      this.overlay?.remove();
    }

    handleHandleTap() {
      if (this.isDragging) return;
      if (this.isOpen) {
        this.close();
      }
    }

    onTouchStart(e) {
      this.dragStartY = e.touches[0].clientY;
      this.dragCurrentY = this.dragStartY;
      this.isDragging = false;
      if (this.expandedEl) {
        this.expandedEl.style.transition = 'none';
      }
    }

    onTouchMove(e) {
      this.dragCurrentY = e.touches[0].clientY;
      const delta = this.dragCurrentY - this.dragStartY;
      if (delta > 5) {
        this.isDragging = true;
      }
      if (this.isDragging && delta > 0 && this.isOpen) {
        e.preventDefault();
        if (this.expandedEl) {
          this.expandedEl.style.transform = `translateY(${delta}px)`;
        }
      }
    }

    onTouchEnd() {
      const delta = this.dragCurrentY - this.dragStartY;
      if (this.expandedEl) {
        this.expandedEl.style.transition = '';
        this.expandedEl.style.transform = '';
      }
      if (this.isDragging && delta > 60 && this.isOpen) {
        this.close();
      }
      setTimeout(() => { this.isDragging = false; }, 50);
    }

    handleTriggerClick() {
      const sizePanel = this.querySelector('[data-sheet-panel^="size-"]');
      if (sizePanel) {
        this.openPanel(sizePanel.dataset.sheetPanel);
      } else {
        const firstPanel = this.querySelector('[data-sheet-panel]');
        if (firstPanel) this.openPanel(firstPanel.dataset.sheetPanel);
      }
    }

    openPanel(panelName) {
      this.panels.forEach(p => p.classList.remove('is-active'));
      const target = this.querySelector(`[data-sheet-panel="${panelName}"]`);
      if (target) {
        target.classList.add('is-active');
        this.currentPanel = panelName;
      }
      this.open();
    }

    open() {
      this.isOpen = true;
      this.classList.add('is-open');
      this.expandedEl?.removeAttribute('aria-hidden');
      this.overlay?.classList.add('is-active');
      this.scrollY = window.scrollY || window.pageYOffset;
      document.body.style.top = `-${this.scrollY}px`;
      document.body.classList.add('mobile-sheet-open');
    }

    close() {
      this.isOpen = false;
      this.classList.remove('is-open');
      this.expandedEl?.setAttribute('aria-hidden', 'true');
      this.overlay?.classList.remove('is-active');
      document.body.classList.remove('mobile-sheet-open');
      document.body.style.top = '';
      window.scrollTo(0, this.scrollY);
      this.panels.forEach(p => p.classList.remove('is-active'));
    }

    handleSizeSelect(event) {
      const btn = event.currentTarget;
      const value = btn.dataset.sizeValue;
      const optionPosition = parseInt(btn.dataset.optionPosition, 10);

      const variant = this.variantsData.find(v => {
        const optKey = `option${optionPosition}`;
        return v[optKey] === value && v.available;
      }) || this.variantsData.find(v => {
        const optKey = `option${optionPosition}`;
        return v[optKey] === value;
      });

      if (!variant) return;
      this.addToCart(variant.id);
    }

    async addToCart(variantId) {
      this.setLoading(true);
      this.clearError();
      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          this.showError(data.description || data.message || 'Unable to add to cart');
          return;
        }
        let cart = null;
        try {
          const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
          cart = await cartResponse.json();
        } catch (_) {}
        if (typeof theme !== 'undefined' && theme.pubsub) {
          theme.pubsub.publish(theme.pubsub.events.CART_UPDATE, {
            source: 'mobile-variant-sheet',
            productVariantId: variantId,
            cartData: data,
            cart,
            skipFetch: !!cart,
          });
        }
        this.openPanel('confirmation');
      } catch (error) {
        console.error('Error adding to cart:', error);
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      this.classList.toggle('is-loading', isLoading);
    }

    showError(message) {
      const activePanel = this.querySelector('[data-sheet-panel].is-active') || this;
      let errorEl = activePanel.querySelector('.mobile-variant-sheet__error');
      if (!errorEl) {
        errorEl = document.createElement('p');
        errorEl.className = 'mobile-variant-sheet__error';
        errorEl.setAttribute('role', 'alert');
        activePanel.appendChild(errorEl);
      }
      errorEl.textContent = message;
    }

    clearError() {
      this.querySelectorAll('.mobile-variant-sheet__error').forEach(el => el.remove());
    }
  }
  customElements.define('mobile-variant-sheet', MobileVariantSheet);
}
